import { useMemo } from 'react';
import { useUserOptions, type UserOption } from '@/shared/hooks/useUserOptions';
import { tierLabelOf } from '@/shared/permissions/identity';

export interface ResolvedEmployee {
  /** Display name for the user, or the raw id when the user is unknown. */
  name: string;
  /**
   * Authority-tier label (`Executive` | `Administrator` | `Employee`) derived
   * ONLY from identity.ts — never a raw backend role string (Req 13.1, 13.2).
   */
  tier: string;
}

/**
 * Resolve per-employee analytics rows (keyed by `userId`) into display-safe
 * labels. Names come from the canonical `/api/users` option source; the
 * authority label is derived through `tierLabelOf` so a raw role is never
 * rendered. Unknown ids fall back to the id itself with no invented role.
 */
export function useEmployeeLabels(enabled = true) {
  const { data: users = [], isLoading } = useUserOptions(enabled);

  const byId = useMemo(() => {
    const map = new Map<string, UserOption>();
    for (const u of users) map.set(String(u.id), u);
    return map;
  }, [users]);

  const resolve = useMemo(
    () =>
      (userId: string): ResolvedEmployee => {
        const u = byId.get(String(userId));
        if (!u) return { name: String(userId), tier: '' };
        return { name: u.display_name || String(userId), tier: tierLabelOf(u.role) };
      },
    [byId],
  );

  return { resolve, isLoading };
}
